import React, { useEffect, useMemo, useState } from "react";
import swal from "sweetalert";
import { API_URL } from "../../constant/API_Settings";
import {
  formatInrAmount as formatCurrency,
  downloadReportPdf,
  formatReportDate,
} from "../../utils/reportFormat";
import {
  getReportDateRangeBounds,
  handleReportDateRangeInput,
  todayIso,
} from "../../utils/reportDateRange";

const authHeaders = () => ({
  "Content-Type": "application/json",
  Authorization: "Bearer " + localStorage.getItem("AUTH_TOKEN") + "",
  Accept: "application/json",
});

/**
 * GST filing workbook — GSTR-3B summary, outward supplies, inward ITC, HSN, downloads.
 */
export default function GstFilingReportPanel() {
  const [defaults, setDefaults] = useState(null);
  const [inputValues, setInputValues] = useState({
    start_date: "",
    end_date: "",
    period_type: "monthly",
  });
  const [errors, setErrors] = useState({});
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState("");

  const today = todayIso();
  const dateBounds = useMemo(
    () => getReportDateRangeBounds({
      startDate: inputValues.start_date,
      endDate: inputValues.end_date,
      today,
    }),
    [inputValues.start_date, inputValues.end_date, today],
  );

  useEffect(() => {
    fetch(`${API_URL}/statutory-filing/date-defaults`, { headers: authHeaders() })
      .then((r) => r.json())
      .then((data) => {
        if (data.status === false) {
          return;
        }
        setDefaults(data);
        setInputValues((prev) => ({
          ...prev,
          start_date: data.monthly?.start_date || prev.start_date,
          end_date: data.monthly?.end_date || prev.end_date,
          period_type: "monthly",
        }));
      })
      .catch(() => {});
  }, []);

  const applyPreset = (type) => {
    const preset = type === "quarterly" ? defaults?.quarterly : defaults?.monthly;
    if (!preset) {
      return;
    }
    setInputValues({
      start_date: preset.start_date,
      end_date: preset.end_date,
      period_type: type,
    });
    setReport(null);
  };

  const handleInput = (e) => {
    if (e.target.name === "start_date" || e.target.name === "end_date") {
      handleReportDateRangeInput(e, inputValues, setInputValues, { today });
      setReport(null);
      return;
    }
    setInputValues({ ...inputValues, [e.target.name]: e.target.value });
  };

  const validateForm = (start = inputValues.start_date, end = inputValues.end_date) => {
    const errs = {};
    let ok = true;
    if (!start) {
      errs.start_date = "Choose start date";
      ok = false;
    }
    if (!end) {
      errs.end_date = "Choose end date";
      ok = false;
    }
    setErrors(errs);
    return ok;
  };

  const payload = () => ({
    start_date: inputValues.start_date,
    end_date: inputValues.end_date,
    period_type: inputValues.period_type,
  });

  const loadReport = () => {
    if (!validateForm()) {
      return;
    }
    setLoading(true);
    setReport(null);
    fetch(`${API_URL}/statutory-filing/gst`, {
      method: "post",
      headers: authHeaders(),
      body: JSON.stringify(payload()),
    })
      .then((r) => r.json())
      .then((data) => {
        setLoading(false);
        if (data.status === false) {
          swal("", data.message || "Unable to load GST report", "warning");
          return;
        }
        setReport(data);
      })
      .catch(() => {
        setLoading(false);
        swal("", "Unable to load GST report. Please try again.", "warning");
      });
  };

  const downloadFile = async (kind) => {
    if (!validateForm()) {
      return;
    }
    setDownloading(kind);
    try {
      if (kind === "pdf") {
        await downloadReportPdf(
          `${API_URL}/statutory-filing/gst/pdf`,
          payload(),
          "GstFilingReport.pdf"
        );
        return;
      }

      const response = await fetch(`${API_URL}/statutory-filing/gst/excel`, {
        method: "post",
        headers: authHeaders(),
        body: JSON.stringify(payload()),
      });
      if (!response.ok) {
        throw new Error("Unable to download the file.");
      }
      const blob = await response.blob();
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.download = "GstFilingReport.xlsx";
      link.click();
      window.URL.revokeObjectURL(link.href);
    } catch (error) {
      swal("", error.message || "Unable to download the file. Please try again.", "warning");
    } finally {
      setDownloading("");
    }
  };

  const moneyCell = (value) => (
    <td className="text-end report-amount">{formatCurrency(value)}</td>
  );

  return (
    <div className="statutory-filing-gst">
      <p className="text-muted small mb-3">
        Monthly or quarterly GST calculations for GSTR-1 / GSTR-3B filing — outward supplies,
        inward ITC, and net payable. Verify with GSTR-2B and your CA before portal upload.
      </p>

      <div className="row mb-3">
        <div className="col-md-12 d-flex flex-wrap align-items-end gap-2">
          <div className="form-group mt-2">
            <label>Filing period</label>
            <select
              className="form-control"
              name="period_type"
              value={inputValues.period_type}
              onChange={handleInput}
            >
              <option value="monthly">Monthly</option>
              <option value="quarterly">Quarterly</option>
            </select>
          </div>
          <div className="form-group mt-2">
            <label>Start date<sup className="star">*</sup></label>
            <input
              type="date"
              className="form-control"
              name="start_date"
              value={inputValues.start_date}
              min={dateBounds.startMin || undefined}
              max={dateBounds.startMax || undefined}
              onChange={handleInput}
            />
            <div className="errorMsg">{errors.start_date}</div>
          </div>
          <div className="form-group mt-2">
            <label>End date<sup className="star">*</sup></label>
            <input
              type="date"
              className="form-control"
              name="end_date"
              value={inputValues.end_date}
              min={dateBounds.endMin || undefined}
              max={dateBounds.endMax || undefined}
              onChange={handleInput}
            />
            <div className="errorMsg">{errors.end_date}</div>
          </div>
          <div className="d-flex flex-wrap gap-2 mt-2">
            <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => applyPreset("monthly")}>
              Last month{defaults?.monthly?.label ? `: ${defaults.monthly.label}` : ""}
            </button>
            <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => applyPreset("quarterly")}>
              Last quarter{defaults?.quarterly?.label ? `: ${defaults.quarterly.label}` : ""}
            </button>
            <button type="button" className="btn btn-primary btn-sm" onClick={loadReport} disabled={loading}>
              {loading ? "Loading…" : "View report"}
            </button>
            {report ? (
              <>
                <button
                  type="button"
                  className="btn btn-success btn-sm"
                  onClick={() => downloadFile("pdf")}
                  disabled={!!downloading}
                >
                  {downloading === "pdf" ? "Downloading…" : "Download PDF"}
                </button>
                <button
                  type="button"
                  className="btn btn-success btn-sm"
                  onClick={() => downloadFile("excel")}
                  disabled={!!downloading}
                >
                  {downloading === "excel" ? "Downloading…" : "Download Excel"}
                </button>
              </>
            ) : null}
          </div>
        </div>
      </div>

      {report ? (
        <>
          <div className="dash-card owner-chart-card mb-3 p-3">
            <div className="d-flex flex-wrap justify-content-between gap-2 mb-2">
              <div>
                <h5 className="mb-1">{report.pump?.name}</h5>
                <p className="small text-muted mb-0">
                  GSTIN: {report.pump?.gstin || "—"} · PAN: {report.pump?.pan || "—"} ·{" "}
                  {report.period?.filing_label} ({report.period?.label})
                </p>
              </div>
              <div className="text-end">
                <span className="badge bg-primary">Net GST payable</span>
                <div className="h4 mb-0 text-danger">
                  {formatCurrency(report.totals?.net_payable?.total)}
                </div>
                {(report.totals?.outward?.total_gst > 0 || report.totals?.inward_itc?.total_gst > 0) ? (
                  <p className="small text-muted mb-0 mt-1">
                    Output GST {formatCurrency(report.totals?.outward?.total_gst)} · ITC{" "}
                    {formatCurrency(report.totals?.inward_itc?.total_gst)}
                  </p>
                ) : null}
              </div>
            </div>
            {(report.summary_notes || []).length > 0 ? (
              <div className="alert alert-warning py-2 px-3 small mb-0 mt-2">
                <strong>Why these figures?</strong>
                <ul className="mb-0 ps-3">
                  {report.summary_notes.map((note) => (
                    <li key={note}>{note}</li>
                  ))}
                </ul>
              </div>
            ) : null}
          </div>

          <h6 className="mb-2">GSTR-3B summary (calculation aid)</h6>
          <div className="table-responsive mb-4">
            <table className="table table-sm owner-analytics-table mb-0">
              <thead>
                <tr>
                  <th>Sec</th>
                  <th>Description</th>
                  <th className="text-end">Taxable value</th>
                  <th className="text-end">IGST</th>
                  <th className="text-end">CGST</th>
                  <th className="text-end">SGST</th>
                </tr>
              </thead>
              <tbody>
                {(report.gstr3b_summary || []).map((row) => (
                  <tr key={row.section}>
                    <td>{row.section}</td>
                    <td>{row.label}</td>
                    {row.taxable_value != null ? moneyCell(row.taxable_value) : <td className="text-end">—</td>}
                    {moneyCell(row.igst)}
                    {moneyCell(row.cgst)}
                    {moneyCell(row.sgst)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <h6 className="mb-2">Outward supplies</h6>
          <div className="table-responsive mb-4">
            <table className="table table-sm owner-analytics-table mb-0">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Type</th>
                  <th>Product</th>
                  <th>HSN</th>
                  <th className="text-end">Taxable</th>
                  <th className="text-end">CGST</th>
                  <th className="text-end">SGST</th>
                  <th className="text-end">IGST</th>
                </tr>
              </thead>
              <tbody>
                {(report.outward_supplies || []).length === 0 ? (
                  <tr><td colSpan={8} className="text-muted">No outward GST supplies in this period.</td></tr>
                ) : (
                  report.outward_supplies.map((row, index) => (
                    <tr key={`out-${index}`}>
                      <td>{formatReportDate(row.date)}</td>
                      <td className="small">{row.supply_type}</td>
                      <td>{row.product}</td>
                      <td>{row.hsn || "—"}</td>
                      {moneyCell(row.taxable_value)}
                      {moneyCell(row.cgst)}
                      {moneyCell(row.sgst)}
                      {moneyCell(row.igst)}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <h6 className="mb-2">Inward supplies / ITC</h6>
          <div className="table-responsive mb-4">
            <table className="table table-sm owner-analytics-table mb-0">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Invoice</th>
                  <th>Vendor</th>
                  <th>Product</th>
                  <th className="text-end">Taxable</th>
                  <th className="text-end">ITC (GST)</th>
                </tr>
              </thead>
              <tbody>
                {(report.inward_supplies_itc || []).length === 0 ? (
                  <tr><td colSpan={6} className="text-muted">No inward GST purchases in this period.</td></tr>
                ) : (
                  report.inward_supplies_itc.map((row, index) => (
                    <tr key={`in-${index}`}>
                      <td>{formatReportDate(row.date)}</td>
                      <td>{row.document_no || "—"}</td>
                      <td>{row.party}</td>
                      <td>{row.product}</td>
                      {moneyCell(row.taxable_value)}
                      {moneyCell(row.total_gst)}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <h6 className="mb-2">HSN summary</h6>
          <div className="table-responsive mb-3">
            <table className="table table-sm owner-analytics-table mb-0">
              <thead>
                <tr>
                  <th>HSN</th>
                  <th className="text-end">Taxable</th>
                  <th className="text-end">Total GST</th>
                </tr>
              </thead>
              <tbody>
                {(report.hsn_summary || []).map((row) => (
                  <tr key={row.hsn}>
                    <td>{row.hsn}</td>
                    {moneyCell(row.taxable_value)}
                    {moneyCell(row.total_gst)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p className="small text-muted">{report.disclaimer}</p>
        </>
      ) : null}
    </div>
  );
}
